import { useEffect, useRef } from 'react';

// Atrapa el foco DENTRO de un contenedor mientras está activo (modales): al abrir, mueve el foco
// adentro; Tab/Shift+Tab ciclan sin salir; Esc llama onEsc; al cerrar, devuelve el foco a donde estaba.
// Compañero de LayerOverlay/useScrollLock (fondo oscuro + scroll bloqueado): juntos dan el comportamiento
// de modal estándar (oscurecido, scroll bloqueado, Esc, clic fuera, foco atrapado) a TODO diálogo.
const SEL_FOCO = 'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export default function useFocusTrap(ref, { active = true, onEsc = null } = {}) {
  const onEscRef = useRef(onEsc);
  onEscRef.current = onEsc;

  useEffect(() => {
    if (!active) return undefined;
    const node = ref.current;
    const previo = document.activeElement;

    const focusables = () => (node ? Array.from(node.querySelectorAll(SEL_FOCO)) : []);
    // Al abrir, el foco entra al modal (primer control, o el contenedor si no hay).
    const primero = focusables()[0];
    (primero || node)?.focus?.();

    const onKey = (e) => {
      if (e.key === 'Escape') { if (onEscRef.current) onEscRef.current(); return; }
      if (e.key !== 'Tab' || !node) return;
      const f = focusables();
      if (f.length === 0) { e.preventDefault(); node.focus?.(); return; }
      const ini = f[0];
      const fin = f[f.length - 1];
      const act = document.activeElement;
      // Si el foco se fue fuera del modal (o al contenedor), lo traemos al extremo que corresponde.
      if (e.shiftKey) {
        if (act === ini || !node.contains(act)) { e.preventDefault(); fin.focus(); }
      } else if (act === fin || !node.contains(act)) { e.preventDefault(); ini.focus(); }
    };

    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      // Devolver el foco a quien lo tenía antes de abrir (si sigue en el documento).
      if (previo && typeof previo.focus === 'function' && document.contains(previo)) previo.focus();
    };
  }, [active, ref]);
}
